import React, { useState } from 'react';

import crops from 'domains/crops';
import photoModule from 'domains/photos';
import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import UserPhotoThumb from './UserPhotoThumb';

export type PhotoSortIndicator = `${string}-${'left' | 'right'}`;

function Filmstrip() {
  const dispatch = useAppDispatch();
  const canvasIds = useAppSelector(canvases.selectors.selectIds);
  const potentialCropIds = useAppSelector(crops.selectors.potentialCropIds);
  const isCartProduct = useAppSelector(activeEditor.selectors.isCartProduct);
  const photos = useAppSelector(photoModule.selectors.allForActiveCollection);

  // A dash separated string matching the photo ID and position (left/right) that a dragged photo will sort around
  const [activeSortIndicator, setActiveSortIndicator] = useState<PhotoSortIndicator | null>(null);

  const canUseMultiPhotos =
    canvasIds.length === 1 && potentialCropIds.length === 1 && !isCartProduct;
  /*
   * Redux values
   */

  const handleImgDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const { currentTarget, clientX } = e;
    setActiveSortIndicator(null);

    const dropPhotoId = parseInt(e.dataTransfer.getData('text/plain'));
    const centerPoints = Array.from(currentTarget.querySelectorAll('img')).map((c) => {
      const { x, width } = c.getBoundingClientRect();
      return x + width / 2;
    });

    const dropBeforeIndex = centerPoints.findIndex((p) => p > clientX);
    dispatch(
      photoModule.actions.sortPhotos({ insertPhotoId: dropPhotoId, beforeIndex: dropBeforeIndex })
    );
  };

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();

    const target = e.target as HTMLImageElement | undefined | null;
    if (!target || !target.getAttribute('data-id')) return;

    const { clientX } = e;
    const { x, width } = target.getBoundingClientRect();
    const center = x + width / 2;

    // Only try to add this sort indicator if we're very sure we have all the data
    if (clientX && center) {
      const targetPhotoId = target.getAttribute('data-id');
      const sortLocation = clientX > center ? 'right' : 'left';
      setActiveSortIndicator(`${targetPhotoId}-${sortLocation}`);
    }
  }

  function handleDragLeave() {
    setActiveSortIndicator(null);
  }

  return (
    <div
      className="scroll_container thumbs_container"
      id="thumbs_container"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleImgDrop}
    >
      <ul className="thumbs_list thumbs sortable" id="photos_view">
        {(photos || []).map((photo, i) => (
          <UserPhotoThumb
            key={photo.id}
            id={i === 0 ? 'first-photo-thumb' : undefined}
            photo={photo}
            canUseMultiPhotos={canUseMultiPhotos}
            activeSortIndicator={activeSortIndicator}
          />
        ))}
      </ul>
    </div>
  );
}

export default Filmstrip;
